import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { previewArticle, publishPreviewedArticle, generatePreviewImages, buildArticleWithImages } from '../services/api';
import AdminHeader from './AdminHeader';

// ─── Constants ────────────────────────────────────────────────
const CONTENT_STYLES = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual & Friendly' },
  { value: 'technical', label: 'Technical & Detailed' },
  { value: 'simple', label: 'Simple & Easy to Read' },
];

const ARTICLE_CATEGORIES = [
  { value: '', label: '— Select a category (required) —' },
  { value: 'longevity', label: '🧬 Longevity — Science of Aging' },
  { value: 'nutrition', label: '🥗 Nutrition — Diet & Metabolic Health' },
  { value: 'research', label: '🔬 Research — Clinical Trials & Discoveries' },
  { value: 'mental-health', label: '🧠 Mental Health — Psychology & Neurological Wellness' },
  { value: 'prevention', label: '🏃 Prevention — Actionable Daily Habits' },
];

const IMAGE_STYLES = [
  { value: 'realistic', label: 'Realistic Photos' },
  { value: 'illustration', label: 'Illustration' },
  { value: 'minimal', label: 'Minimal & Clean' },
  { value: 'vibrant', label: 'Vibrant & Colorful' },
];

// Steps: 1=Write form, 2=Edit article+placeholders, 3=Review preview images, 4=Final preview
const STEPS = [
  { id: 1, label: 'Write',   icon: '✍️' },
  { id: 2, label: 'Edit',    icon: '📝' },
  { id: 3, label: 'Images',  icon: '🎨' },
  { id: 4, label: 'Publish', icon: '🚀' },
];

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

// ─── Placeholder image factory (client-side SVG, no network needed) ───────────
const makePlaceholderDataUri = (num) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="340">
    <rect width="800" height="340" fill="#f0f2f5" rx="10"/>
    <rect x="340" y="110" width="120" height="90" rx="16" fill="#dde1e7"/>
    <text x="400" y="168" font-family="Arial,sans-serif" font-size="40" text-anchor="middle" fill="#b0b8c8">&#128247;</text>
    <text x="400" y="230" font-family="Arial,sans-serif" font-size="20" font-weight="bold" text-anchor="middle" fill="#b0b8c8">Image ${num}</text>
    <text x="400" y="260" font-family="Arial,sans-serif" font-size="13" text-anchor="middle" fill="#c8cdd6">Placeholder - will be replaced with generated image</text>
  </svg>`;
  // btoa requires latin1; encode via encodeURIComponent to handle any unicode safely
  const encoded = encodeURIComponent(svg).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)));
  return `data:image/svg+xml;base64,${btoa(encoded)}`;
};

// Inject placeholder images into article body HTML (client-side DOMParser)
const injectPlaceholders = (bodyHtml, count) => {
  if (count <= 0) return bodyHtml;
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="root">${bodyHtml}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return bodyHtml;

  const makeDiv = (n) => {
    const div = doc.createElement('div');
    div.className = 'article-placeholder-img';
    div.innerHTML = `<img src="${makePlaceholderDataUri(n)}" alt="Image ${n} placeholder" style="width:100%;border-radius:10px;display:block;margin:20px 0;">`;
    return div;
  };

  root.insertBefore(makeDiv(1), root.firstChild);

  const elems = root.querySelectorAll('p, h2, h3');
  if (count > 1 && elems.length > 0) {
    const interval = Math.max(1, Math.floor(elems.length / count));
    for (let i = 1; i < count; i++) {
      const after = elems[Math.min(i * interval, elems.length - 1)];
      after.parentNode.insertBefore(makeDiv(i + 1), after.nextSibling);
    }
  }
  return root.innerHTML;
};

// Build absolute image URL for local backend uploads
const toAbsoluteUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) return path;
  return `${API_BASE}${path}`;
};

// ─── Main Component ───────────────────────────────────────────
export default function WriteArticlePage() {
  // ── Step 1 form state ──
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [includeSummary, setIncludeSummary] = useState(true);
  const [contentStyle, setContentStyle] = useState('professional');
  const [imageCount, setImageCount] = useState(2);
  // ── Step navigation ──
  const [step, setStep] = useState(1);

  // ── Step 2 (Edit article) ──
  const [articleData, setArticleData] = useState(null); // { title, html, bodyHtml, summary, topic, keywords }
  const [editTitle, setEditTitle] = useState('');
  const [editBodyHtml, setEditBodyHtml] = useState('');
  const [isEditingHtml, setIsEditingHtml] = useState(false);
  const [articleWithPlaceholders, setArticleWithPlaceholders] = useState(''); // body HTML with placeholder images

  // ── Step 3 (Preview images) ──
  const [imageBatches, setImageBatches] = useState([]); // [{ id, images[], source, style, count, prompt }]
  const [selectedImages, setSelectedImages] = useState([]); // ordered array of selected image paths
  const [genPage, setGenPage] = useState(1); // tracks next page to request (for Pexels/Unsplash variety)

  // ── Per-generation settings (Step 3 panel) ──
  const [genSource, setGenSource] = useState('pexels');
  const [genStyle, setGenStyle] = useState('realistic');
  const [genCount, setGenCount] = useState(2);
  const [genPrompt, setGenPrompt] = useState('');
  const [genRefImage, setGenRefImage] = useState(null); // File object
  const [genRefPreview, setGenRefPreview] = useState(''); // data-url for thumbnail

  // ── Step 4 (Final preview) ──
  const [finalData, setFinalData] = useState(null); // { title, html, imageUrl, summary, topic }

  // ── Published ──
  const [published, setPublished] = useState(null);

  // ── Loading / error ──
  const [generatingArticle, setGeneratingArticle] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [buildingFinal, setBuildingFinal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');

  // ─── Handlers ───────────────────────────────────────────────

  // Step 1 → 2: Generate article text only (imageMode: 'none')
  const handleGenerateArticle = async () => {
    if (!title.trim()) { setError('Please enter a title.'); return; }
    if (!content.trim()) { setError('Please enter article content.'); return; }
    if (!category) { setError('Please select a category before continuing.'); return; }
    setError('');
    setGeneratingArticle(true);

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('includeSummary', String(includeSummary));
      formData.append('contentStyle', contentStyle);
      formData.append('imageMode', 'none'); // never generate images in this step

      const response = await previewArticle(formData);
      if (!response.success) {
        setError(response.message || 'Failed to generate article.');
        return;
      }

      const data = response.data;
      setArticleData(data);
      setEditTitle(data.title);
      setEditBodyHtml(data.bodyHtml || '');

      // Inject placeholder images into the body for visual preview
      const withPlaceholders = injectPlaceholders(data.bodyHtml || '', imageCount);
      setArticleWithPlaceholders(withPlaceholders);

      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Request failed.');
    } finally {
      setGeneratingArticle(false);
    }
  };

  // When user edits the body HTML in textarea – update bodyHtml and re-inject placeholders
  const handleBodyHtmlChange = (newHtml) => {
    setEditBodyHtml(newHtml);
    setArticleWithPlaceholders(injectPlaceholders(newHtml, imageCount));
  };

  // Step 2 → 3: Just navigate; user generates images from Step 3 panel
  const handleProceedToImages = () => {
    setImageBatches([]);
    setSelectedImages([]);
    setGenPage(1);
    setGenPrompt('');
    setGenRefImage(null);
    setGenRefPreview('');
    setStep(3);
  };

  // Step 3: Generate a new batch using the current per-generation settings
  const handleAddGeneration = async () => {
    if (!articleData?.topic) { setError('No article topic found.'); return; }
    setError('');
    setGeneratingImages(true);

    // Reference images require DALL-E; auto-switch
    const effectiveSource = genRefImage ? 'dalle' : genSource;

    try {
      const response = await generatePreviewImages({
        topic: articleData.topic,
        keywords: articleData.keywords || [],
        imageCount: genCount,
        imageSource: effectiveSource,
        imageStyle: genStyle,
        previewMode: true,
        page: genPage,
        customPrompt: genPrompt.trim() || null,
        refImage: genRefImage || null,
      });

      if (!response.success) {
        setError(response.message || 'Image generation failed.');
        return;
      }

      const images = response.data.images || [];
      const isFirst = imageBatches.length === 0;
      const newBatch = { id: Date.now(), images, source: effectiveSource, style: genStyle, count: genCount, prompt: genPrompt.trim() };
      setImageBatches(prev => [...prev, newBatch]);
      setGenPage(prev => prev + 1);

      // Auto-select all images from the very first generation
      if (isFirst) setSelectedImages(images);

      // Clear the reference image after use (prompt retained for reuse)
      setGenRefImage(null);
      setGenRefPreview('');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Image generation failed.');
    } finally {
      setGeneratingImages(false);
    }
  };

  // Step 3 → 4: User approves images — build final article HTML with selected images
  const handleApproveImages = async () => {
    setError('');
    setBuildingFinal(true);

    try {
      const response = await buildArticleWithImages({
        title: editTitle,
        bodyHtml: editBodyHtml,
        summary: articleData?.summary || '',
        imagePaths: selectedImages,
      });

      if (!response.success) {
        setError(response.message || 'Failed to build final article.');
        return;
      }

      setFinalData({
        title: editTitle,
        html: response.data.html,
        imageUrl: response.data.imageUrl,
        summary: articleData?.summary || '',
        topic: articleData?.topic || '',
      });
      setStep(4);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to build article.');
    } finally {
      setBuildingFinal(false);
    }
  };

  // Step 4: Publish
  const handlePublish = async () => {
    if (!finalData) return;
    setPublishing(true);
    setError('');
    try {
      const response = await publishPreviewedArticle({
        title: finalData.title,
        html: finalData.html,
        imageUrl: finalData.imageUrl,
        topic: finalData.topic,
        summary: finalData.summary,
        category,
      });
      if (response.success) {
        setPublished(response.data);
        // Reset everything
        setStep(1);
        setTitle(''); setContent(''); setCategory('');
        setArticleData(null); setEditTitle(''); setEditBodyHtml('');
        setImageBatches([]); setSelectedImages([]); setGenPage(1); setFinalData(null);
      } else {
        setError(response.message || 'Failed to publish.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Publish failed.');
    } finally {
      setPublishing(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────

  return (
    <div className="app-wrapper">
      <AdminHeader
        title="Write Article"
        subtitle="Author in any language — translate, rewrite & publish"
      />
      <div className="app">
        <div style={{ margin: '0 auto' }}>

          {/* ── Published Success Banner ──────────────────────── */}
          {published && (
            <div style={{
              background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '8px',
              padding: '18px 20px', marginBottom: '24px', color: '#2e7d32'
            }}>
              <strong>✅ Article published as featured!</strong>
              <div style={{ marginTop: '6px', fontSize: '0.9em', color: '#333' }}>
                <span style={{ fontWeight: 600 }}>{published.title}</span>
              </div>
              <div style={{ marginTop: '8px', display: 'flex', gap: '10px' }}>
                <a href="http://localhost:8000" target="_blank" rel="noopener noreferrer"
                  style={{ ...navBtn('#4CAF50'), fontSize: '0.85em', padding: '6px 14px' }}>
                  📺 View on Public Site ↗
                </a>
                <Link to="/admin-preview" style={{ ...navBtn('#1976D2'), fontSize: '0.85em', padding: '6px 14px' }}>
                  📰 Layout Editor
                </Link>
              </div>
            </div>
          )}

          {/* ── Step Progress Bar ─────────────────────────────── */}
          <StepBar currentStep={step} />

          {/* ── Error Banner ──────────────────────────────────── */}
          {error && (
            <div style={{
              background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: '8px',
              padding: '14px 18px', marginBottom: '20px', color: '#c62828', fontSize: '0.92em'
            }}>
              ❌ {error}
              <button onClick={() => setError('')} style={{
                float: 'right', background: 'none', border: 'none', cursor: 'pointer',
                color: '#c62828', fontSize: '1.1em', lineHeight: 1
              }}>✕</button>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* STEP 1: Write form                                 */}
          {/* ══════════════════════════════════════════════════ */}
          {step === 1 && (
            <>
              <Card title="📝 Article Title">
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={generatingArticle}
                  placeholder="Enter article title (any language)…"
                  style={inputStyle}
                />
                <div style={{ fontSize: '0.78em', color: '#888', marginTop: '5px' }}>
                  You can write in Hindi, Spanish, French, Arabic, or any language — it will be translated to English.
                </div>
              </Card>

              <Card title="📄 Article Content">
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  disabled={generatingArticle}
                  placeholder="Write or paste the full article content here (any language)…"
                  rows={12}
                  style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                />
              </Card>

              <Card title="🏷️ Article Category">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={generatingArticle}
                  style={{
                    ...selectStyle,
                    fontSize: '0.95em',
                    border: !category ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '6px',
                    padding: '10px 12px',
                  }}
                >
                  {ARTICLE_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                {!category && (
                  <div style={{ fontSize: '0.82em', color: '#f44336', marginTop: '6px', fontWeight: 500 }}>
                    ⚠️ Category is required — the article will appear under this section on the public site.
                  </div>
                )}
                {category && (
                  <div style={{ fontSize: '0.82em', color: '#2e7d32', marginTop: '6px' }}>
                    ✅ Will be published under <strong>{ARTICLE_CATEGORIES.find(c => c.value === category)?.label.split('—')[0].trim()}</strong> and on the Home page.
                  </div>
                )}
              </Card>

              <Card title="⚙️ Writing Options">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={labelStyle}>✍️ Writing Style</label>
                    <select value={contentStyle} onChange={(e) => setContentStyle(e.target.value)}
                      disabled={generatingArticle} style={selectStyle}>
                      {CONTENT_STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', ...labelStyle, cursor: 'pointer' }}>
                      <input type="checkbox" checked={includeSummary}
                        onChange={(e) => setIncludeSummary(e.target.checked)} disabled={generatingArticle} />
                      <span>💡 Include Summary</span>
                    </label>
                  </div>
                </div>
              </Card>

              <Card title="🖼️ Image Settings (configured in Step 3)">
                <div style={{
                  background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px',
                  padding: '10px 14px', fontSize: '0.85em', color: '#795548'
                }}>
                  ℹ️ Images are generated <strong>separately</strong> in Step 3 — source, style, count, custom prompt, and reference images are all configured there.
                </div>
              </Card>

              {generatingArticle ? (
                <LoadingBox message="✨ Translating & rewriting your article…" note="This takes 15–30 seconds" />
              ) : (
                <button
                  onClick={handleGenerateArticle}
                  disabled={!title.trim() || !content.trim() || !category}
                  style={{
                    width: '100%', padding: '14px', fontSize: '1.05em', fontWeight: 600,
                    background: '#1976D2', color: 'white', border: 'none', borderRadius: '8px',
                    cursor: (!title.trim() || !content.trim() || !category) ? 'not-allowed' : 'pointer',
                    opacity: (!title.trim() || !content.trim() || !category) ? 0.6 : 1,
                  }}
                >
                  ✨ Generate Article Text
                </button>
              )}
            </>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* STEP 2: Edit article + placeholder images          */}
          {/* ══════════════════════════════════════════════════ */}
          {step === 2 && articleData && (
            <>
              <Card title="📝 Article Title (editable)">
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  style={{ ...inputStyle, fontSize: '1.05em', fontWeight: 600 }}
                />
              </Card>

              {articleData.summary && (
                <Card title="💡 Summary">
                  <div style={{
                    background: '#f8f9fa', padding: '14px 16px', borderLeft: '4px solid #3498db',
                    fontStyle: 'italic', color: '#555', fontSize: '0.93em', borderRadius: '0 6px 6px 0'
                  }}>
                    {articleData.summary}
                  </div>
                </Card>
              )}

              <Card title="📄 Article Preview (with placeholder images)">
                <div style={{
                  background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px',
                  padding: '8px 14px', marginBottom: '14px', fontSize: '0.83em', color: '#795548'
                }}>
                  🖼️ Grey placeholder images show where real images will appear. Click <strong>"Generate Preview Images"</strong> below to replace them.
                </div>

                {/* Toggle between rendered view and raw HTML edit */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <button onClick={() => setIsEditingHtml(false)}
                    style={{
                      padding: '6px 16px', fontSize: '0.85em', borderRadius: '6px', cursor: 'pointer',
                      background: !isEditingHtml ? '#1976D2' : '#f5f5f5',
                      color: !isEditingHtml ? 'white' : '#555',
                      border: !isEditingHtml ? 'none' : '1px solid #ddd',
                    }}>
                    👁️ Preview
                  </button>
                  <button onClick={() => setIsEditingHtml(true)}
                    style={{
                      padding: '6px 16px', fontSize: '0.85em', borderRadius: '6px', cursor: 'pointer',
                      background: isEditingHtml ? '#1976D2' : '#f5f5f5',
                      color: isEditingHtml ? 'white' : '#555',
                      border: isEditingHtml ? 'none' : '1px solid #ddd',
                    }}>
                    ✏️ Edit HTML
                  </button>
                </div>

                {isEditingHtml ? (
                  <>
                    <textarea
                      value={editBodyHtml}
                      onChange={(e) => handleBodyHtmlChange(e.target.value)}
                      rows={20}
                      style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.82em', resize: 'vertical' }}
                    />
                    <div style={{ fontSize: '0.78em', color: '#888', marginTop: '4px' }}>
                      Editing raw HTML body. Placeholder images are added automatically in the preview.
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px',
                      background: '#fafafa', maxHeight: '600px', overflow: 'auto',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      lineHeight: '1.8', fontSize: '1em'
                    }}
                    dangerouslySetInnerHTML={{ __html: articleWithPlaceholders }}
                  />
                )}
              </Card>

              <Card title="🖼️ Placeholder Settings">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px' }}>
                  <div>
                    <label style={labelStyle}>📷 Number of placeholder images: {imageCount}</label>
                    <input type="range" min="1" max="5" value={imageCount}
                      onChange={(e) => {
                        const n = parseInt(e.target.value);
                        setImageCount(n);
                        setArticleWithPlaceholders(injectPlaceholders(editBodyHtml, n));
                      }}
                      style={{ width: '100%', marginTop: '6px' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#888' }}>
                      <span>1</span><span>5</span>
                    </div>
                    <div style={{ fontSize: '0.78em', color: '#888', marginTop: '6px' }}>
                      Adjust how many placeholder images appear in the preview above. Real image count is determined by your selection in the next step.
                    </div>
                  </div>
                </div>
                {articleData.topic && (
                  <div style={{ marginTop: '12px', fontSize: '0.83em', color: '#666' }}>
                    Detected topic: <strong>{articleData.topic}</strong>
                    {articleData.keywords?.length > 0 && (
                      <span style={{ marginLeft: '8px', color: '#999' }}>
                        · keywords: {articleData.keywords.slice(0, 4).join(', ')}
                      </span>
                    )}
                  </div>
                )}
              </Card>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setStep(1)}
                  style={{ ...secondaryBtn, flex: '0 0 auto', padding: '12px 20px' }}>
                  ← Back to Edit
                </button>
                <button onClick={handleProceedToImages}
                  style={{ ...primaryBtn('#7B1FA2'), flex: 1, padding: '14px' }}>
                  🎨 Proceed to Image Selection →
                </button>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* STEP 3: Review preview images                      */}
          {/* ══════════════════════════════════════════════════ */}
          {step === 3 && (
            <>
              {/* ── Selection tray ───────────────────────────── */}
              <Card title={`✅ Selected Images (${selectedImages.length}) — in article order`}>
                {selectedImages.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#bbb', fontSize: '0.9em' }}>
                    No images selected yet. Generate images below and click to select them.
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {selectedImages.map((imgPath, idx) => (
                      <div key={imgPath} style={{ position: 'relative', width: '100px' }}>
                        <div style={{
                          position: 'absolute', top: '-6px', left: '-6px', zIndex: 2,
                          background: '#1976D2', color: 'white', borderRadius: '50%',
                          width: '22px', height: '22px', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.75em', fontWeight: 700, boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}>{idx + 1}</div>
                        <button
                          onClick={() => setSelectedImages(prev => prev.filter(p => p !== imgPath))}
                          style={{
                            position: 'absolute', top: '-6px', right: '-4px', zIndex: 2,
                            background: '#e53935', color: 'white', border: 'none',
                            borderRadius: '50%', width: '20px', height: '20px',
                            cursor: 'pointer', fontSize: '12px', lineHeight: '1',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)', padding: 0
                          }}
                          title="Remove"
                        >✕</button>
                        <img
                          src={toAbsoluteUrl(imgPath)}
                          alt={`Selected ${idx + 1}`}
                          style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #4CAF50', display: 'block' }}
                          onError={(e) => { e.target.src = makePlaceholderDataUri(idx + 1); }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* ── Generation settings panel ─────────────────── */}
              <Card title="🎨 Generate Images">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={labelStyle}>🌐 Image Source</label>
                    <select value={genSource} onChange={(e) => setGenSource(e.target.value)} style={selectStyle} disabled={generatingImages}>
                      <option value="pexels">📷 Pexels (Free Stock)</option>
                      <option value="unsplash">🌅 Unsplash (Free Stock)</option>
                      <option value="dalle">🎨 DALL-E (AI Generated)</option>
                      <option value="gemini">✨ Gemini (AI Generated)</option>
                    </select>
                    {genRefImage && genSource !== 'dalle' && (
                      <div style={{ fontSize: '0.75em', color: '#e65100', marginTop: '4px' }}>
                        ⚠️ Reference image uses DALL-E automatically
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={labelStyle}>🎨 Image Style</label>
                    <select value={genStyle} onChange={(e) => setGenStyle(e.target.value)} style={selectStyle} disabled={generatingImages}>
                      {IMAGE_STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>📷 Count: {genCount}</label>
                    <input type="range" min="1" max="5" value={genCount}
                      onChange={(e) => setGenCount(parseInt(e.target.value))}
                      disabled={generatingImages}
                      style={{ width: '100%', marginTop: '6px' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#888' }}>
                      <span>1</span><span>5</span>
                    </div>
                  </div>
                </div>

                {/* Custom text prompt */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>✏️ Custom Prompt (optional)</label>
                  <textarea
                    value={genPrompt}
                    onChange={(e) => setGenPrompt(e.target.value)}
                    disabled={generatingImages}
                    placeholder={`Leave blank to auto-search for: "${articleData?.topic || 'article topic'}"`}
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical', fontSize: '0.88em' }}
                  />
                  <div style={{ fontSize: '0.75em', color: '#888', marginTop: '3px' }}>
                    For Pexels/Unsplash: used as the search query. For DALL-E/Gemini: used as the image prompt.
                  </div>
                </div>

                {/* Reference image upload */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>🖼️ Reference Image (optional — DALL-E only)</label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {genRefPreview ? (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <img src={genRefPreview} alt="Reference" style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #7B1FA2' }} />
                        <button
                          onClick={() => { setGenRefImage(null); setGenRefPreview(''); }}
                          style={{
                            position: 'absolute', top: '-6px', right: '-6px',
                            background: '#e53935', color: 'white', border: 'none',
                            borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer',
                            fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0
                          }}>✕</button>
                      </div>
                    ) : (
                      <label style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 14px', border: '1.5px dashed #bbb', borderRadius: '6px',
                        cursor: 'pointer', fontSize: '0.85em', color: '#555', background: '#fafafa',
                        flexShrink: 0
                      }}>
                        📤 Upload image
                        <input type="file" accept="image/*" style={{ display: 'none' }} disabled={generatingImages}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setGenRefImage(file);
                            const reader = new FileReader();
                            reader.onload = (ev) => setGenRefPreview(ev.target.result);
                            reader.readAsDataURL(file);
                          }} />
                      </label>
                    )}
                    <div style={{ fontSize: '0.78em', color: '#888', paddingTop: '4px' }}>
                      Upload an image and DALL-E will generate variations in a similar style/composition.<br />
                      <span style={{ color: '#aaa' }}>JPEG or PNG recommended. Max 10 MB.</span>
                    </div>
                  </div>
                </div>

                {generatingImages ? (
                  <LoadingBox
                    message="🎨 Generating images…"
                    note={(genRefImage || genSource === 'dalle') ? 'DALL-E — ~30–60 sec per image' : 'Fetching stock photos…'}
                  />
                ) : (
                  <button onClick={handleAddGeneration}
                    style={{ ...primaryBtn('#7B1FA2'), width: '100%', padding: '13px', fontSize: '1em' }}>
                    {imageBatches.length === 0 ? '🎨 Generate Images' : '🔄 Generate Another Set'}
                  </button>
                )}
              </Card>

              {/* ── All batches, newest first ─────────────────── */}
              {[...imageBatches].reverse().map((batch, reversedIdx) => {
                const genNum = imageBatches.length - reversedIdx;
                const labelParts = [
                  batch.source === 'pexels' ? 'Pexels' : batch.source === 'unsplash' ? 'Unsplash' : batch.source === 'gemini' ? 'Gemini' : 'DALL-E',
                  batch.style,
                  `${batch.images.length} images`,
                ];
                if (batch.prompt) labelParts.push(`"${batch.prompt.slice(0, 30)}${batch.prompt.length > 30 ? '…' : ''}"`);
                return (
                  <Card key={batch.id} title={`Generation ${genNum} — ${labelParts.join(' · ')}`}>
                    <div style={{ fontSize: '0.83em', color: '#666', marginBottom: '12px' }}>
                      Click to <strong>add</strong> or <strong>remove</strong> from your selection. The number shows order in the final article.
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: batch.images.length === 1 ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))',
                      gap: '14px'
                    }}>
                      {batch.images.map((imgPath) => {
                        const selIdx = selectedImages.indexOf(imgPath);
                        const isSelected = selIdx !== -1;
                        return (
                          <div
                            key={imgPath}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedImages(prev => prev.filter(p => p !== imgPath));
                              } else {
                                setSelectedImages(prev => [...prev, imgPath]);
                              }
                            }}
                            style={{
                              border: isSelected ? '3px solid #4CAF50' : '2px solid #e0e0e0',
                              borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
                              background: isSelected ? '#f1f8e9' : '#fafafa',
                              position: 'relative',
                              boxShadow: isSelected ? '0 0 0 2px rgba(76,175,80,0.2)' : 'none',
                            }}
                          >
                            {isSelected && (
                              <div style={{
                                position: 'absolute', top: '8px', right: '8px', zIndex: 1,
                                background: '#4CAF50', color: 'white', borderRadius: '50%',
                                width: '28px', height: '28px', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '13px', boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                              }}>{selIdx + 1}</div>
                            )}
                            <img
                              src={toAbsoluteUrl(imgPath)}
                              alt={`Gen ${genNum}`}
                              style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
                              onError={(e) => { e.target.src = makePlaceholderDataUri(1); }}
                            />
                            <div style={{
                              padding: '8px 12px', fontSize: '0.82em',
                              color: isSelected ? '#2e7d32' : '#777',
                              fontWeight: isSelected ? 600 : 400,
                            }}>
                              {isSelected ? `✅ #${selIdx + 1} in article` : '+ click to select'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}

              {/* ── Bottom action bar ─────────────────────────── */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setStep(2)} style={{ ...secondaryBtn, flex: '0 0 auto', padding: '12px 20px' }}>
                  ← Back
                </button>
                {buildingFinal ? (
                  <div style={{ flex: 1, ...loadingInlinePill }}>⏳ Building article with {selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''}…</div>
                ) : (
                  <button onClick={handleApproveImages}
                    disabled={selectedImages.length === 0}
                    title={selectedImages.length === 0 ? 'Select at least one image to continue' : `Build article with ${selectedImages.length} image${selectedImages.length !== 1 ? 's' : ''}`}
                    style={{
                      ...primaryBtn('#4CAF50'), flex: 1, padding: '12px',
                      opacity: selectedImages.length === 0 ? 0.5 : 1,
                      cursor: selectedImages.length === 0 ? 'not-allowed' : 'pointer'
                    }}>
                    ✅ Use {selectedImages.length > 0 ? `${selectedImages.length} Selected Image${selectedImages.length !== 1 ? 's' : ''}` : 'Selected Images'} →
                  </button>
                )}
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* STEP 4: Final article preview                      */}
          {/* ══════════════════════════════════════════════════ */}
          {step === 4 && finalData && (
            <>
              <div style={{
                background: '#fff', border: '2px solid #4CAF50', borderRadius: '10px',
                marginBottom: '24px', overflow: 'hidden'
              }}>
                <div style={{
                  background: '#4CAF50', color: 'white', padding: '14px 20px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <span style={{ fontWeight: 600, fontSize: '1.05em' }}>🔍 Final Preview — Review before publishing</span>
                  <span style={{ fontSize: '0.82em', opacity: 0.85 }}>Not saved yet</span>
                </div>
                <div style={{ padding: '20px' }}>
                  <h2 style={{ margin: '0 0 8px 0', fontSize: '1.5em', color: '#1a1a1a' }}>{finalData.title}</h2>
                  {finalData.topic && (
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '12px' }}>
                      Topic: {finalData.topic}
                    </div>
                  )}
                  {finalData.summary && (
                    <div style={{
                      background: '#f8f9fa', padding: '14px 16px', borderLeft: '4px solid #3498db',
                      marginBottom: '16px', fontStyle: 'italic', color: '#555', fontSize: '0.92em', borderRadius: '0 6px 6px 0'
                    }}>
                      {finalData.summary}
                    </div>
                  )}
                  <div
                    style={{
                      border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px',
                      background: '#fafafa', maxHeight: '600px', overflow: 'auto',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      lineHeight: '1.8'
                    }}
                    dangerouslySetInnerHTML={{ __html: (() => {
                      // Extract just the body content from the full HTML document for display
                      try {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(finalData.html, 'text/html');
                        // Fix relative image URLs to absolute
                        doc.querySelectorAll('img').forEach(img => {
                          const src = img.getAttribute('src');
                          if (src && !src.startsWith('http') && !src.startsWith('data:')) {
                            img.setAttribute('src', `${API_BASE}${src}`);
                          }
                        });
                        return doc.body.innerHTML;
                      } catch {
                        return finalData.html;
                      }
                    })() }}
                  />
                </div>
                <div style={{
                  padding: '16px 20px', borderTop: '1px solid #e0e0e0',
                  display: 'flex', gap: '12px', justifyContent: 'flex-end', background: '#f5f5f5'
                }}>
                  <button onClick={() => setStep(3)} disabled={publishing}
                    style={{ ...secondaryBtn, opacity: publishing ? 0.6 : 1 }}>
                    ← Back to Images
                  </button>
                  <button onClick={() => setStep(2)} disabled={publishing}
                    style={{ ...secondaryBtn, opacity: publishing ? 0.6 : 1 }}>
                    ✏️ Edit Article
                  </button>
                  <button onClick={handlePublish} disabled={publishing}
                    style={{
                      padding: '10px 28px', fontSize: '0.95em', fontWeight: 600,
                      background: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px',
                      cursor: publishing ? 'not-allowed' : 'pointer', opacity: publishing ? 0.7 : 1
                    }}>
                    {publishing ? '⏳ Publishing…' : '🚀 Publish as Featured'}
                  </button>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────

function StepBar({ currentStep }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', marginBottom: '24px',
      background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px',
      padding: '14px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
    }}>
      {STEPS.map((s, idx) => (
        <React.Fragment key={s.id}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: '1.1em',
              background: s.id < currentStep ? '#4CAF50' : s.id === currentStep ? '#1976D2' : '#e0e0e0',
              color: s.id <= currentStep ? 'white' : '#999',
              fontWeight: 600, marginBottom: '4px',
              border: s.id === currentStep ? '3px solid #1565C0' : '3px solid transparent',
              transition: 'all 0.3s'
            }}>
              {s.id < currentStep ? '✓' : s.icon}
            </div>
            <div style={{
              fontSize: '0.75em', fontWeight: s.id === currentStep ? 700 : 400,
              color: s.id === currentStep ? '#1976D2' : s.id < currentStep ? '#4CAF50' : '#999'
            }}>
              {s.label}
            </div>
          </div>
          {idx < STEPS.length - 1 && (
            <div style={{
              flex: 2, height: '3px', margin: '0 4px', marginBottom: '18px',
              background: s.id < currentStep ? '#4CAF50' : '#e0e0e0',
              borderRadius: '2px', transition: 'background 0.3s'
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px',
      padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
    }}>
      <h3 style={{ margin: '0 0 14px 0', fontSize: '0.95em', color: '#333', fontWeight: 600 }}>{title}</h3>
      {children}
    </div>
  );
}

function LoadingBox({ message, note }) {
  return (
    <div style={{
      background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '8px',
      padding: '20px', textAlign: 'center', marginTop: '8px'
    }}>
      <div style={{ fontSize: '1.15em', marginBottom: '6px' }}>
        <span className="spinner" style={{ marginRight: '10px' }} />
        {message}
      </div>
      {note && (
        <div style={{ fontSize: '0.82em', color: '#555', marginTop: '4px' }}>{note}</div>
      )}
    </div>
  );
}

// ─── Style helpers ────────────────────────────────────────────
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px',
  border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95em', outline: 'none'
};

const selectStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #ddd',
  borderRadius: '6px', fontSize: '0.88em', background: '#fff'
};

const labelStyle = {
  display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '6px', fontWeight: 500
};

const secondaryBtn = {
  padding: '10px 20px', fontSize: '0.95em', fontWeight: 500,
  background: 'white', color: '#555', border: '1px solid #ccc', borderRadius: '6px',
  cursor: 'pointer'
};

const primaryBtn = (bg) => ({
  padding: '10px 20px', fontSize: '0.95em', fontWeight: 600,
  background: bg, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'
});

const loadingInlinePill = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '6px',
  color: '#555', fontSize: '0.92em'
};

const navBtn = (bg) => ({
  padding: '8px 16px', textDecoration: 'none', backgroundColor: bg,
  color: 'white', borderRadius: '4px', border: 'none', display: 'inline-block',
  fontSize: '0.9em', cursor: 'pointer'
});
